import React, { useRef, useState, useEffect } from "react";
import {
  Typography,
  Button,
  message,
  Modal,
  Empty,
  Tag,
  Tooltip,
  TimePicker,
  Select,
} from "antd";
import {
  GripVertical,
  Trash2,
  Play,
  Plus,
  Crown,
  Send,
  Clock,
  Image as ImageIcon,
  Video,
  ListPlus,
  ArrowRight,
  Eye,
  FolderOpen,
  MonitorPlay,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import moment from "moment";
import PreviewPlayer from "../components/PreviewPlayer";
import LibraryPickerModal from "../components/LibraryPickerModal";
import LiteAPI from "../api/liteAPI";
import { useLanguage, UI_TRANSLATIONS } from "../LanguageContext";

const { Title, Text } = Typography;

const stagger = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.05 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 300, damping: 24 },
  },
};

const itemVariant = {
  hidden: { opacity: 0, y: 10 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 300, damping: 24 },
  },
  exit: { opacity: 0, x: -30, transition: { duration: 0.2 } },
};

const DURATION_FORMAT = "HH:mm:ss";

const secondsToMoment = (seconds) => moment.utc((seconds || 0) * 1000);

const momentToSeconds = (time) => {
  if (!time) return null;
  return time.hours() * 3600 + time.minutes() * 60 + time.seconds();
};

const LoopPlaylist = ({
  playlistItems,
  setPlaylistItems,
  libraryList,
  setLibraryList,
  onNavigate,
  hasScreen,
  selectedRatio,
  apiProps,
  brandData,
  playlistId,
  screenData,
  refreshPlaylist,
  refreshLibrary,
  allScreens = [],
  activePlaylistScreenId,
  onSwitchPlaylistScreen,
}) => {
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);
  const [draggingIndex, setDraggingIndex] = useState(null);
  const probedIds = useRef(new Set());

  // Probe real video duration for items whose duration is still the default 10s
  useEffect(() => {
    playlistItems.forEach((item) => {
      if (item.type !== "video") return;
      const src = item.url || item.previewUrl;
      if (!src) return;

      if (
        !probedIds.current.has(item.id) &&
        (item.durationSeconds === 10 || item.durationSeconds === undefined)
      ) {
        probedIds.current.add(item.id);
        const vid = document.createElement("video");
        vid.preload = "metadata";
        vid.onloadedmetadata = () => {
          const secs = Math.round(vid.duration);
          if (!secs || isNaN(secs)) return;
          const fmt =
            secs >= 60 ? `${Math.floor(secs / 60)}m ${secs % 60}s` : `${secs}s`;
          setPlaylistItems((prev) =>
            prev.map((li) =>
              li.id === item.id
                ? { ...li, duration: fmt, durationSeconds: secs }
                : li,
            ),
          );
        };
        vid.src = src;
      }
    });
  }, [playlistItems]);
  const [showPreview, setShowPreview] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [isTouchLayout, setIsTouchLayout] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 1024 : false,
  );
  const { lang } = useLanguage();
  const t = UI_TRANSLATIONS[lang].playlist;
  const howItWorksBody = isTouchLayout
    ? t.howItWorksTextTouch || t.howItWorksText
    : t.howItWorksText;

  useEffect(() => {
    const onResize = () => setIsTouchLayout(window.innerWidth <= 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Guards against the same selection being submitted twice (e.g. a fast
  // double-click on "Add" before the picker modal has visually closed),
  // which was causing every added item to appear twice.
  const addInFlightRef = useRef(false);

  const handleAddFromPicker = async (items) => {
    if (addInFlightRef.current) return;
    addInFlightRef.current = true;
    try {
      const itemArray = Array.isArray(items) ? items : [items];

      if (apiProps && brandData && playlistId) {
        // ── Optimistic: add all at once with unique pending IDs ──
        const pendingItems = itemArray.map((item, i) => ({
          ...item,
          id: `pending-${Date.now()}-${i}`,
        }));
        setPlaylistItems((prev) => [...prev, ...pendingItems]);

        // ── Sequential API calls — one at a time to avoid race conditions ──
        for (let i = 0; i < itemArray.length; i++) {
          const item = itemArray[i];
          const pendingId = pendingItems[i].id;
          try {
            const fileId = item.raw?.FileID || item.id;
            await LiteAPI.addMediaToPlaylist(
              playlistId,
              fileId,
              brandData.BrandID,
              apiProps,
            );
            message.success(t.addSuccess(item.name));
          } catch (err) {
            message.error(t.addFail);
            console.error("Add to playlist error:", err);
            // Roll back only this specific pending item
            setPlaylistItems((prev) => prev.filter((p) => p.id !== pendingId));
          }
        }
        // Refresh after all API calls complete so server state is consistent
        if (refreshPlaylist) await refreshPlaylist();
      } else {
        // ── Fallback: local mock ──
        const newItems = itemArray.map((item) => ({
          ...item,
          id:
            "pl-" + Date.now() + "-" + Math.random().toString(36).substr(2, 4),
        }));
        setPlaylistItems((prev) => [...prev, ...newItems]);
        itemArray.forEach((item) => message.success(t.addSuccess(item.name)));
      }
    } finally {
      addInFlightRef.current = false;
    }
  };

  const handleDragStart = (e, index) => {
    dragItem.current = index;
    setDraggingIndex(index);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", index.toString());
    }
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    dragOverItem.current = index;
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    dragOverItem.current = index;
    if (
      dragItem.current !== null &&
      dragOverItem.current !== null &&
      dragItem.current !== dragOverItem.current
    ) {
      const items = [...playlistItems];
      const draggedItem = items.splice(dragItem.current, 1)[0];
      items.splice(dragOverItem.current, 0, draggedItem);
      setPlaylistItems(items);
      // Note: Server-side reorder can be implemented later via update_media API
    }
    dragItem.current = null;
    dragOverItem.current = null;
    setDraggingIndex(null);
  };

  const handleDragEnd = () => {
    dragItem.current = null;
    dragOverItem.current = null;
    setDraggingIndex(null);
  };

  const [movedId, setMovedId] = useState(null);

  const handleMoveItem = (index, delta) => {
    const next = index + delta;
    if (next < 0 || next >= playlistItems.length) return;
    const items = [...playlistItems];
    [items[index], items[next]] = [items[next], items[index]];
    setPlaylistItems(items);
    // Brief highlight on the moved item
    setMovedId(items[next].id);
    setTimeout(() => setMovedId(null), 600);
  };

  const handleDelete = async (id) => {
    const item = playlistItems.find((i) => i.id === id);
    const itemName = item?.name || item?.title || "";

    Modal.confirm({
      title: t.deleteConfirmTitle,
      content: t.deleteConfirmContent(itemName),
      okText: t.deleteConfirmOk,
      cancelText: t.deleteConfirmCancel,
      okButtonProps: { danger: true },
      onOk: async () => {
        if (apiProps && brandData) {
          // ── Real delete via API ──
          // Remove locally right away so the item disappears instantly,
          // instead of waiting on a refetch that can lag behind the write.
          const previousItems = playlistItems;
          setPlaylistItems((prev) => prev.filter((p) => p.id !== id));
          try {
            const mediaId = item?.raw?.MediaID || id;
            await LiteAPI.deleteMedia(mediaId, apiProps);
            message.success(t.removeSuccess);
            if (refreshPlaylist) {
              await refreshPlaylist();
              // Defensive: if the server's read hasn't caught up with the
              // delete yet, refreshPlaylist() could bring the item back —
              // make sure it stays removed.
              setPlaylistItems((prev) => prev.filter((p) => p.id !== id));
            }
          } catch (err) {
            message.error(t.removeFail);
            console.error("Delete media error:", err);
            // Roll back the optimistic removal since the delete failed
            setPlaylistItems(previousItems);
          }
        } else {
          // ── Fallback: local mock ──
          setPlaylistItems(
            playlistItems.filter((playlistItem) => playlistItem.id !== id),
          );
          message.success(t.removeSuccess);
        }
      },
    });
  };

  const handleClearAll = () => {
    Modal.confirm({
      title: t.clearTitle,
      content: t.clearContent,
      okText: t.clearOk,
      okButtonProps: { danger: true },
      onOk: async () => {
        if (apiProps && brandData) {
          // ── Real clear via API ── (delete each media item)
          try {
            for (const item of playlistItems) {
              const mediaId = item.raw?.MediaID || item.id;
              await LiteAPI.deleteMedia(mediaId, apiProps);
            }
            // Clear locally right away rather than waiting on a refetch
            // that can still return the now-deleted items for a moment.
            setPlaylistItems([]);
            message.success(t.clearSuccess);
            if (refreshPlaylist) {
              await refreshPlaylist();
              setPlaylistItems([]);
            }
          } catch (err) {
            message.error(t.clearFail);
            if (refreshPlaylist) await refreshPlaylist();
          }
        } else {
          setPlaylistItems([]);
          message.success(t.clearSuccess);
        }
      },
    });
  };

  const handlePublish = async () => {
    if (!hasScreen) {
      message.warning(t.pairFirst);
      return;
    }
    if (playlistItems.length === 0) {
      message.warning(t.addFirst);
      return;
    }
    if (apiProps && screenData?.raw) {
      // ── Real publish via API ──
      try {
        await LiteAPI.publishContent([screenData.raw], apiProps);
        message.success(t.publishedSuccess(playlistItems.length));
      } catch (err) {
        message.error(t.publishFail);
        console.error("Publish error:", err);
      }
    } else {
      message.success(t.publishedSuccess(playlistItems.length));
    }
  };

  const handleAdvancedScheduling = () => {
    Modal.info({
      icon: <Crown color="#f59e0b" size={24} />,
      title: t.proTitle,
      content: t.proContent,
      okText: t.gotIt,
    });
  };

  const handleDurationChange = async (item, selectedTime) => {
    const newSecs = momentToSeconds(selectedTime);
    if (!newSecs || newSecs < 1) return;
    const fmt =
      newSecs >= 60
        ? `${Math.floor(newSecs / 60)}m ${newSecs % 60}s`
        : `${newSecs}s`;
    setPlaylistItems((prev) =>
      prev.map((li) =>
        li.id === item.id
          ? {
              ...li,
              duration: fmt,
              durationSeconds: newSecs,
              raw: { ...(li.raw || {}), Duration: newSecs },
            }
          : li,
      ),
    );
    if (apiProps) {
      try {
        const mediaId = item.raw?.MediaID || item.id;
        await LiteAPI.updateMedia(mediaId, { duration: newSecs }, apiProps);
      } catch (err) {
        console.error("Update duration error:", err);
      }
    }
  };

  // Calculate total duration
  const totalDuration = playlistItems.reduce((sum, item) => {
    return sum + (item.durationSeconds || 10);
  }, 0);

  const formatDuration = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  };

  return (
    <motion.div variants={stagger} initial="hidden" animate="show">
      {/* ── No-screen banner ── */}
      {!hasScreen && (
        <motion.div variants={fadeUp} style={{ marginBottom: "20px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
              padding: "14px 20px",
              borderRadius: "12px",
              background: "#fffbeb",
              border: "1px solid #fde68a",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <MonitorPlay size={18} color="#d97706" />
              <Text
                style={{ fontSize: "13px", color: "#92400e", fontWeight: 500 }}
              >
                {t.pairBanner}
              </Text>
            </div>
            <Button
              size="small"
              onClick={() => onNavigate("screen")}
              style={{
                borderRadius: "8px",
                borderColor: "#fbbf24",
                color: "#d97706",
                fontWeight: 600,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {t.pairBannerBtn}
            </Button>
          </div>
        </motion.div>
      )}

      {/* ── Screen Selector dropdown (shown when 2 screens are paired) ── */}
      {allScreens.length >= 2 && onSwitchPlaylistScreen && (
        <motion.div variants={fadeUp} style={{ marginBottom: "16px" }}>
          <Select
            value={activePlaylistScreenId}
            onChange={(val) => onSwitchPlaylistScreen(val)}
            style={{ width: "100%" }}
            size="large"
            optionLabelProp="label"
          >
            {allScreens.slice(0, 2).map((screen, idx) => (
              <Select.Option
                key={screen.id}
                value={screen.id}
                label={
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <MonitorPlay size={14} color="#6366f1" />
                    <span
                      style={{ fontWeight: 600 }}
                    >{`Screen ${idx + 1}`}</span>
                  </span>
                }
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "4px 0",
                  }}
                >
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "8px",
                      background:
                        activePlaylistScreenId === screen.id
                          ? "#ede9fe"
                          : "#f1f5f9",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <MonitorPlay
                      size={14}
                      color={
                        activePlaylistScreenId === screen.id
                          ? "#6366f1"
                          : "#94a3b8"
                      }
                    />
                  </div>
                  <div>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: "13px",
                        color: "#0f172a",
                      }}
                    >
                      {`Screen ${idx + 1}`}
                    </div>
                    <div style={{ fontSize: "11px", color: "#94a3b8" }}>
                      {screen.name || screen.ScreenName || "LiteScreen"}
                    </div>
                  </div>
                </div>
              </Select.Option>
            ))}
          </Select>
        </motion.div>
      )}

      {/* ── Header ── */}
      <motion.div variants={fadeUp} className="lite-section-header">
        <div>
          <h2>{t.title}</h2>
          <p>{t.subtitle}</p>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          {/* <Tooltip title="Pro feature">
            <Button
              icon={<Crown size={14} color="#f59e0b" />}
              onClick={handleAdvancedScheduling}
              style={{
                borderRadius: "8px",
                borderColor: "#fde68a",
                color: "#f59e0b",
              }}
            >
              {t.scheduleBtn}
            </Button>
          </Tooltip> */}
          <Button
            icon={<Eye size={14} />}
            onClick={() => setShowPreview(true)}
            disabled={playlistItems.length === 0}
            style={{ borderRadius: "8px", fontWeight: 600 }}
          >
            {t.preview}
          </Button>
          <Button
            type="primary"
            icon={<Send size={14} />}
            onClick={handlePublish}
            disabled={playlistItems.length === 0 || !hasScreen}
            className={
              playlistItems.length > 0 && hasScreen ? "lite-publish-btn" : ""
            }
            style={{
              borderRadius: "8px",
              fontWeight: 600,
              background:
                playlistItems.length > 0 && hasScreen ? "#6366f1" : undefined,
              borderColor:
                playlistItems.length > 0 && hasScreen ? "#6366f1" : undefined,
            }}
          >
            {t.publish}
          </Button>
        </div>
      </motion.div>

      {/* ── Stats Bar ── */}
      {playlistItems.length > 0 && (
        <motion.div variants={fadeUp} className="lite-stats-bar">
          <div className="lite-stats-group">
            <Play size={14} color="#6366f1" />
            <Text className="lite-stats-text">
              <Text strong style={{ color: "#0f172a" }}>
                {playlistItems.length}
              </Text>{" "}
              {t.items}
            </Text>
          </div>
          <div className="lite-stats-divider" />
          <div className="lite-stats-group">
            <Clock size={14} color="#6366f1" />
            <Text className="lite-stats-text">
              {t.totalLoop}{" "}
              <Text strong style={{ color: "#0f172a" }}>
                {formatDuration(totalDuration)}
              </Text>
            </Text>
          </div>
          <div className="lite-stats-clear">
            <Button
              type="text"
              danger
              size="small"
              onClick={handleClearAll}
              style={{ fontSize: "12px" }}
            >
              {t.clearAll}
            </Button>
          </div>
        </motion.div>
      )}

      {/* ── Playlist Items ── */}
      {playlistItems.length === 0 ? (
        <motion.div
          variants={fadeUp}
          className="lite-empty-state"
          style={{ marginTop: "8px" }}
        >
          <div className="icon-circle">
            <ListPlus size={32} color="#6366f1" />
          </div>
          <Title level={4} style={{ marginBottom: "8px", color: "#0f172a" }}>
            {t.emptyTitle}
          </Title>
          <Text
            type="secondary"
            style={{
              display: "block",
              maxWidth: "380px",
              margin: "0 auto 24px",
              fontSize: "14px",
              lineHeight: "1.6",
            }}
          >
            {t.emptyText}
          </Text>
          <Button
            type="primary"
            icon={<FolderOpen size={14} />}
            onClick={() => setShowPicker(true)}
            style={{
              borderRadius: "8px",
              background: "#6366f1",
              borderColor: "#6366f1",
            }}
          >
            {t.addContent}
          </Button>
        </motion.div>
      ) : (
        <motion.div
          variants={fadeUp}
          className="lite-card no-hover"
          style={{ padding: 0, overflow: "hidden" }}
        >
          {/* List Header */}
          <div className="lite-panel-header">
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <Play size={16} color="#6366f1" />
              <Text strong style={{ fontSize: "13px" }}>
                {t.activeLoop}
              </Text>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Text
                type="secondary"
                className="lite-drag-hint"
                style={{ fontSize: "12px", whiteSpace: "nowrap" }}
              >
                {t.dragToReorder}
              </Text>
              <Text
                type="secondary"
                className="lite-move-hint"
                style={{ fontSize: "12px", whiteSpace: "nowrap" }}
              >
                ▲▼ to reorder
              </Text>
              {/* Add from library — always visible in header */}
              <div
                onClick={() => setShowPicker(true)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  cursor: "pointer",
                  color: "#6366f1",
                  fontWeight: 600,
                  fontSize: "13px",
                  padding: "5px 10px 5px 5px",
                  borderRadius: "8px",
                  border: "1.5px dashed #c7d2fe",
                  background: "#f5f3ff",
                  transition: "all 0.18s ease",
                  whiteSpace: "nowrap",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#ede9fe";
                  e.currentTarget.style.borderColor = "#818cf8";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#f5f3ff";
                  e.currentTarget.style.borderColor = "#c7d2fe";
                }}
              >
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: "6px",
                    background: "#6366f1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Plus size={13} color="#ffffff" strokeWidth={2.5} />
                </div>
                {t.addMore}
              </div>
            </div>
          </div>

          <AnimatePresence initial={false} mode="popLayout">
            {playlistItems.map((item, index) => {
              const itemClass = `lite-playlist-item ${draggingIndex === index ? "dragging" : ""} ${isTouchLayout && movedId === item.id ? "lite-item-moved" : ""}`;
              const innerContent = (
                <>
                  <div
                    className="lite-item-main"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "14px",
                      flex: 1,
                      overflow: "hidden",
                    }}
                  >
                    {/* Desktop: drag handle */}
                    <span className="lite-grip">
                      <GripVertical
                        size={16}
                        color="#cbd5e1"
                        style={{ cursor: "grab", flexShrink: 0 }}
                      />
                    </span>

                    {/* Mobile/tablet: up-down buttons */}
                    <div className="lite-move-btns">
                      <button
                        disabled={index === 0}
                        onClick={() => handleMoveItem(index, -1)}
                        className="lite-move-btn"
                      >
                        <ChevronUp size={13} />
                      </button>
                      <button
                        disabled={index === playlistItems.length - 1}
                        onClick={() => handleMoveItem(index, 1)}
                        className="lite-move-btn"
                      >
                        <ChevronDown size={13} />
                      </button>
                    </div>

                    <div className="order-number">{index + 1}</div>

                    <div className="lite-playlist-thumb">
                      {item.type === "video" ? (
                        <video
                          src={item.previewUrl || item.url}
                          muted
                          preload="metadata"
                        />
                      ) : item.previewUrl ? (
                        <img src={item.previewUrl} alt={item.name} />
                      ) : (
                        <ImageIcon size={20} color="#ec4899" />
                      )}
                    </div>

                    <div style={{ overflow: "hidden" }}>
                      <Text
                        strong
                        style={{
                          display: "block",
                          fontSize: "13px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {item.name}
                      </Text>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          marginTop: "2px",
                        }}
                      >
                        <div
                          style={{
                            border: "1px solid #c7d2fe",
                            borderRadius: "8px",
                            padding: "2px 8px",
                            background: "#ffffff",
                            boxShadow: "0 1px 2px rgba(99,102,241,0.08)",
                            display: "inline-flex",
                            alignItems: "center",
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <TimePicker
                            allowClear={false}
                            bordered={false}
                            showNow={false}
                            suffixIcon={<Clock size={14} color="#6366f1" />}
                            value={secondsToMoment(item.durationSeconds || 10)}
                            onChange={(time) =>
                              handleDurationChange(item, time)
                            }
                            format={DURATION_FORMAT}
                            style={{ width: "90px" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    className="lite-item-controls"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      flexShrink: 0,
                    }}
                  >
                    <Tag
                      style={{
                        margin: 0,
                        borderRadius: "6px",
                        fontSize: "11px",
                        border: "none",
                        background:
                          item.type === "video" ? "#eff6ff" : "#fdf2f8",
                        color: item.type === "video" ? "#3b82f6" : "#ec4899",
                      }}
                    >
                      {item.type === "video" ? "VID" : "IMG"}
                    </Tag>
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<Trash2 size={14} />}
                      onClick={() => handleDelete(item.id)}
                    />
                  </div>
                </>
              );

              if (isTouchLayout) {
                return (
                  <motion.div
                    key={item.id}
                    layout
                    variants={itemVariant}
                    initial="hidden"
                    animate="show"
                    exit="exit"
                    transition={{ type: "spring", stiffness: 380, damping: 28 }}
                    className={itemClass}
                  >
                    {innerContent}
                  </motion.div>
                );
              }

              return (
                <motion.div
                  key={item.id}
                  layout
                  variants={itemVariant}
                  initial="hidden"
                  animate="show"
                  exit="exit"
                  transition={{ type: "spring", stiffness: 380, damping: 28 }}
                >
                  {/*
                    Framer Motion v2 reserves the `onDragStart`/`onDragEnd` prop
                    names for its own pointer-based drag gesture and strips them
                    before forwarding to the DOM, so native HTML5 drag-and-drop
                    handlers must live on a plain element instead of motion.div.
                  */}
                  <div
                    draggable={true}
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragEnter={(e) => handleDragEnter(e, index)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "move";
                    }}
                    onDrop={(e) => handleDrop(e, index)}
                    className={itemClass}
                  >
                    {innerContent}
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>
      )}

      {/* ── Info Card ── */}
      <motion.div variants={fadeUp} style={{ marginTop: "24px" }}>
        <div
          className="lite-card"
          style={{ background: "#f0fdf4", border: "1px solid #bbf7d0" }}
        >
          <Text strong style={{ color: "#166534", fontSize: "13px" }}>
            {t.howItWorks}
          </Text>
          <Text
            style={{
              display: "block",
              color: "#15803d",
              fontSize: "13px",
              marginTop: "6px",
              lineHeight: "1.6",
            }}
          >
            {howItWorksBody.split(t.publishWord).map((part, i, arr) =>
              i < arr.length - 1 ? (
                <span key={i}>
                  {part}
                  <strong>{t.publishWord}</strong>
                </span>
              ) : (
                <span key={i}>{part}</span>
              ),
            )}
          </Text>
        </div>
      </motion.div>

      {/* ── Preview Player ── */}
      <PreviewPlayer
        visible={showPreview}
        onClose={() => setShowPreview(false)}
        playlistItems={playlistItems}
        selectedRatio={selectedRatio}
      />

      {/* ── Library Picker Modal ── */}
      <LibraryPickerModal
        visible={showPicker}
        onClose={() => {
          setShowPicker(false);
        }}
        libraryList={libraryList}
        setLibraryList={setLibraryList}
        onAddToPlaylist={handleAddFromPicker}
        apiProps={apiProps}
        brandData={brandData}
        refreshLibrary={refreshLibrary}
        hasScreen={hasScreen}
      />
    </motion.div>
  );
};

export default LoopPlaylist;
